/**
 * One-shot reload after a deploy invalidates the JS chunks a page still points at.
 *
 * WHY: a tab opened before a deploy keeps the old build's chunk manifest. The first
 * navigation that needs a chunk the new deploy no longer serves fails with
 * `ChunkLoadError` (webpack/Next), "Failed to fetch dynamically imported module"
 * (Vite/Chromium) or "Importing a module script failed" (Safari), and the page is
 * dead until the user reloads by hand. One reload picks up the new manifest.
 *
 * LOOP GUARD: the reload is recorded in sessionStorage BEFORE it happens. A second
 * chunk failure inside `CHUNK_RELOAD_WINDOW_MS` of that mark (the chunk is missing for
 * a reason a reload cannot fix: CDN outage, blocked script) never reloads again, so a
 * broken deploy cannot trap a visitor in a reload loop. After the window a later deploy
 * can recover the same tab once more.
 *
 * SSR-safe and fail-closed: no sessionStorage (blocked, private mode, server) means no
 * reload, because without the marker the loop guard cannot hold.
 */

export const CHUNK_RELOAD_MARKER = 'bk_chunk_reload_at';
export const CHUNK_RELOAD_WINDOW_MS = 10 * 60 * 1000;

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk [\w./-]+ failed|Loading CSS chunk [\w./-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

// Structural stand-ins for the browser globals used below. This package also builds and runs
// server-side without the DOM lib, so it names the shapes it needs instead of `Storage`,
// `Window` or `PromiseRejectionEvent`.
interface MarkerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

type EventListenerLike = (event: unknown) => void;

interface EventTargetLike {
  addEventListener(type: string, listener: EventListenerLike): void;
  removeEventListener(type: string, listener: EventListenerLike): void;
}

interface ChunkReloadEnv {
  storage?: MarkerStorage;
  reload?: () => void;
  now?: () => number;
}

function resolveEnv(): ChunkReloadEnv {
  const g = globalThis as typeof globalThis & {
    sessionStorage?: MarkerStorage;
    location?: { reload: () => void };
  };
  let storage: MarkerStorage | undefined;
  try {
    storage = g.sessionStorage;
  } catch {
    storage = undefined;
  }
  return { storage, reload: () => g.location?.reload(), now: () => Date.now() };
}

/** True for the errors a stale chunk manifest produces, whatever bundler raised them. */
export function isChunkLoadError(error: unknown): boolean {
  if (error == null) return false;
  if (typeof error === 'string') return CHUNK_ERROR_PATTERN.test(error);
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return typeof message === 'string' && CHUNK_ERROR_PATTERN.test(message);
}

/**
 * Reload the page once when `error` is a chunk-load failure. Returns whether a
 * reload was triggered. Never throws.
 */
export function reloadOnceOnChunkLoadError(error: unknown, envOverride?: ChunkReloadEnv): boolean {
  try {
    if (!isChunkLoadError(error)) return false;
    const env = { ...resolveEnv(), ...envOverride };
    const { storage, reload } = env;
    if (!storage || !reload) return false;
    const now = (env.now ?? Date.now)();
    const last = Number(storage.getItem(CHUNK_RELOAD_MARKER));
    if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) return false;
    storage.setItem(CHUNK_RELOAD_MARKER, String(now));
    reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Listen for the chunk failures that never reach a React error boundary: rejected
 * dynamic imports and Vite's `vite:preloadError`. Returns the uninstall function.
 */
export function installChunkLoadReload(envOverride?: ChunkReloadEnv): () => void {
  const candidate = globalThis as Partial<EventTargetLike>;
  if (typeof candidate.addEventListener !== 'function') return () => undefined;
  const target = candidate as EventTargetLike;
  const onRejection: EventListenerLike = (event) =>
    void reloadOnceOnChunkLoadError((event as { reason?: unknown }).reason, envOverride);
  const onError: EventListenerLike = (event) => {
    const { error, message } = event as { error?: unknown; message?: unknown };
    void reloadOnceOnChunkLoadError(error ?? message, envOverride);
  };
  const onVitePreload: EventListenerLike = (event) => {
    const preload = event as { payload?: unknown; preventDefault?: () => void };
    // Vite expects preventDefault() so the failed import's error is not rethrown
    // while the reload is under way.
    if (reloadOnceOnChunkLoadError(preload.payload, envOverride)) {
      preload.preventDefault?.();
    }
  };
  target.addEventListener('unhandledrejection', onRejection);
  target.addEventListener('error', onError);
  target.addEventListener('vite:preloadError', onVitePreload);
  return () => {
    target.removeEventListener('unhandledrejection', onRejection);
    target.removeEventListener('error', onError);
    target.removeEventListener('vite:preloadError', onVitePreload);
  };
}
